import React, { useMemo } from 'react';
import { Col, Row } from 'antd';
import { FormikProps } from 'formik';
import { Input } from 'formik-antd';

import FormItem from 'components/formik/FormItem';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  citiesSelector,
  countriesSelector,
  isCitiesLoadingSelector,
  isCountriesLoadingSelector,
  isStatesLoadingSelector,
  statesSelector,
} from 'redux/selectors/regionSelectors';
import { WorkerInfoFormValues } from '../CompanySection';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';

type Props = {
  formik: FormikProps<WorkerInfoFormValues>;
  isAddCompanyOptionSelected: boolean;
  isZipRequired: boolean;
};
export default function CompanyStateForm(props: Props) {
  const { formik, isAddCompanyOptionSelected, isZipRequired } = props;

  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const states = useAppSelector(statesSelector);
  const isStatesLoading = useAppSelector(isStatesLoadingSelector);
  const cities = useAppSelector(citiesSelector);
  const isCitiesLoading = useAppSelector(isCitiesLoadingSelector);

  const filteredStates = useMemo(
    () =>
      states.filter(
        (state) => state.countryId === formik.values.company.country,
      ),
    [formik.values.company.country, states],
  );

  const filteredCompanyCities = useMemo(
    () => cities.filter((city) => city.stateId === formik.values.company.state),
    [formik.values.company.state, cities],
  );

  const handleCompanyStateChange = () => {
    formik.setFieldValue('company.city', '', false);
    formik.setFieldValue('company.internationalCityName', '', false);
  };

  const handleCountryChange = (
    countryId: number,
    formik: FormikProps<WorkerInfoFormValues>,
  ) => {
    formik.setFieldValue('company.city', '', false);
    formik.setFieldValue('company.state', undefined, false);
    formik.setFieldValue('company.internationalCityName', '', false);
    formik.setFieldValue('company.internationalStateName', '', false);
  };

  return (
    <>
      <Row gutter={40}>
        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <FormItem name="company.country" label="Country *">
                <FormikSelectWithLoading
                  isLoading={isCountriesLoading}
                  name="company.country"
                  showSearch
                  disabled={!isAddCompanyOptionSelected}
                  placeholder="Select country"
                  optionFilterProp="children"
                  onChange={(value: number) =>
                    handleCountryChange(value, formik)
                  }
                >
                  {countries.map((country) => (
                    <FormikSelectWithLoading.Option
                      key={`select-country-${country.id}`}
                      value={country.id || -1}
                    >
                      {country.name}
                    </FormikSelectWithLoading.Option>
                  ))}
                </FormikSelectWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem name="company.state" label="State *">
                <FormikSelectWithLoading
                  isLoading={isStatesLoading}
                  name="company.state"
                  showSearch
                  disabled={
                    !formik.values.company.country ||
                    !isAddCompanyOptionSelected
                  }
                  placeholder="Select state"
                  optionFilterProp="children"
                  onChange={() => handleCompanyStateChange()}
                >
                  {filteredStates.map((state) => (
                    <FormikSelectWithLoading.Option
                      key={`select-state-${state.id}`}
                      value={state.id || -1}
                    >
                      {state.name}
                    </FormikSelectWithLoading.Option>
                  ))}
                </FormikSelectWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem
                label={isZipRequired ? 'ZIP *' : 'ZIP'}
                name="company.zip"
              >
                <Input
                  name="company.zip"
                  placeholder="Enter ZIP code"
                  disabled={!isAddCompanyOptionSelected}
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>

        <Col xs={24} md={12}>
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <FormItem name="company.city" label="City *">
                <FormikAutoCompleteWithLoading
                  isLoading={isCitiesLoading}
                  name="company.city"
                  filterOption
                  placeholder="Enter city"
                  showSearch
                  disabled={
                    !formik.values.company.state || !isAddCompanyOptionSelected
                  }
                >
                  {filteredCompanyCities.map((city) => (
                    <FormikAutoCompleteWithLoading.Option
                      key={`company-city-${city.id}`}
                      value={city.name || ''}
                    >
                      {city.name}
                    </FormikAutoCompleteWithLoading.Option>
                  ))}
                </FormikAutoCompleteWithLoading>
              </FormItem>
            </Col>

            <Col xs={24} lg={12}>
              <FormItem label="Address *" name="company.address">
                <Input
                  name="company.address"
                  placeholder="Enter address"
                  disabled={!isAddCompanyOptionSelected}
                  autoComplete="off"
                />
              </FormItem>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}
